/**
 * Connect — sensor-channel registry (OMON-209 Phase B/C).
 *
 * Maps a device property type (the chip / shelly type from the Firestore
 * registry) to the renderable channels it produces, sourced from the BigQuery
 * `coordinator_sensors` response. One property can yield SEVERAL channels — e.g.
 * HTS221 → a temperature lane and a humidity lane (composite fan-out).
 *
 * Each channel reads from a bucket's `f` map (see sensor-series.ts):
 *   - `column` is the key in `f` (a FIREHOSE column the CF returned).
 *   - dict columns (`hts221`, `tsl2591`, `serviceData`) are parsed objects →
 *     read `field` from within. Scalar columns (`deviceOriginRssi`,
 *     `calibratedPucks`, …) are read directly (no `field`).
 *   - `stateOf` overrides extraction for non-numeric/state channels (booleans,
 *     `window == 0`, `output` 'true'/'false') → 1 (present/closed/on) / 0 / null.
 *
 * Phase C: continuous lanes use the 4-bucket `newThermal` palette (NOT a smooth
 * gradient) with the Flutter per-sensor domains; state lanes are green/red.
 * Event lanes (buttons, motion, push) are the next slice (scatter + a second CF
 * query). See `backlog/docs/OMON-209-bigquery-sensor-source.md`.
 */

import { interpolateViridis } from "d3-scale-chromatic";

export type LaneKind = "gradient" | "event" | "state";

export interface ChannelSpec {
  label: string;
  unit: string;
  kind: LaneKind;
  /** Flutter channel identifier (e.g. "shellyTemp", "TSL2591", "deviceOriginLatency")
   *  — the `subtype` half of the `${propertyPath}::${subtype}` per-user filter key.
   *  MUST match the legacy coordinator's strings so saved filters interop (OMON-212). */
  subtype: string;
  /** Key in a bucket's `f` map — the FIREHOSE column the CF returned. */
  column: string;
  /** For dict columns, the field within the parsed object. Omit for scalars. */
  field?: string;
  /** Optional numeric transform (e.g. abs for RSSI). Continuous channels only. */
  transform?: (v: number) => number;
  /** Override extraction for state/boolean channels → 1 | 0 | null. */
  stateOf?: (raw: unknown) => number | null;
  /** For event channels: which `events[deviceId][].kind` feeds this lane. */
  eventKind?: "button" | "motion" | "push" | "door" | "matMotion" | "matWeight" | "bedExit";
  /** OMON-236: operational event lanes (push-sent) whose records are written to
   *  Firebase _values by agent.pushnotifications — NOT the IoT firehose/BigQuery
   *  the CF reads. Sourced via a direct _values read instead of the CF events. */
  firebaseEvents?: boolean;
  /** OMON-238: for firebaseEvents lanes, the _values timestamp field to window
   *  on (default "timestamp"; door events use "deviceTimestamp"). */
  firebaseTsField?: string;
  /** OMON-238: keep only _values docs where doc[field] === value. Since
   *  2026-07-08 this is ALSO pushed into the Firestore query server-side
   *  (composite indexes on values(deviceEventType,timestamp) and
   *  values(window,deviceTimestamp) exist in prod) — the noise-stamped state
   *  records (~2.7k/day/device, pilot21) made the unfiltered fetch pull ~38k
   *  docs per 14-day view and freeze the tab. A door-open is window===0
   *  (inverted sensor, OMON-232). */
  firebaseMatch?: { field: string; value: number | string | boolean };
  /** For firebaseEvents lanes: the point's value from the doc (default 1), so
   *  one lane can colour its dots by a field. Used by "Push sent" to tell a
   *  carer's push (1) from a family push (2), 2026-10-27. */
  firebaseValue?: (doc: Record<string, unknown>) => number;
  /** For state channels: [label when 0, label when 1] for the hover tooltip. */
  stateLabels?: [string, string];
  /** Value → fill colour. */
  colorFor(value: number | null): string;
  /**
   * OMON-295: which palette a `gradient` lane interpolates through. Omitted =
   * `diverging`, so anything not explicitly reviewed keeps today's appearance.
   */
  palette?: PaletteName;
}

/**
 * OMON-295: a diverging palette (dark blue → pale yellow → red) implies a
 * meaningful MIDPOINT — the pale middle reads as "neutral", the ends as "away
 * from neutral in opposite directions". That is a real claim about the data,
 * and it is true of room temperature (there is a comfort band to be above or
 * below) and false of lux, power, latency and signal strength, where "middle"
 * means nothing and the pale midpoint misreads as low.
 *
 * Those get `sequential` = viridis: perceptually uniform (equal value steps
 * look like equal colour steps, which blue→yellow→red does NOT), monotonic in
 * lightness (so it survives greyscale and low vision), and colour-vision-
 * deficiency safe. It is the standard answer for magnitude-without-a-midpoint.
 */
export type PaletteName = "diverging" | "sequential";

export function paletteColor(palette: PaletteName, t: number): string {
  const clamped = Math.max(0, Math.min(1, isFinite(t) ? t : 0));
  return palette === "sequential" ? interpolateViridis(clamped) : gradientColor(clamped);
}

const GREY = "rgb(180,180,180)";

/**
 * `newThermal` 4-bucket floor-index colour (Flutter `interpolate('newThermal',
 * low, high, v)`) — deep-blue → mid-blue → pale-yellow → red, NOT a smooth
 * gradient. `t = abs((v-low)/(high-low))`; `idx = clamp(floor(4t), 0, 3)`.
 */
const NEW_THERMAL = ["#1e40af", "#2563eb", "#fde68a", "#dc2626"];
function thermal(low: number, high: number): (v: number | null) => string {
  const span = high - low || 1;
  return (v) => {
    if (v === null) return GREY;
    const t = Math.abs((v - low) / span);
    const idx = Math.max(0, Math.min(3, Math.floor(4 * t)));
    return NEW_THERMAL[idx];
  };
}

/**
 * OMON-223: CONTINUOUS interpolation across the same deep-blue → mid-blue →
 * pale-yellow → red control points (the smooth version of `thermal`'s 4-bucket
 * floor-index). `t ∈ [0,1]` is linearly interpolated between adjacent anchors,
 * so every change in value shifts the hue — no quantization.
 *
 * The 4-bucket `thermal()` was a Flutter `interpolate('newThermal', …)` fossil:
 * it could only ever show 4 colours, collapsing a sensor sitting in one quartile
 * (e.g. an H&T near a radiator reading 36–44 °C, all in bucket 3) to a single
 * flat band. The renderer feeds this with a domain auto-scaled to the VISIBLE
 * data, so small variations are visible at any zoom.
 */
const NEW_THERMAL_RGB: [number, number, number][] = [
  [49, 54, 149], [69, 117, 180], [254, 224, 144], [215, 48, 39],
];
export function gradientColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, isFinite(t) ? t : 0));
  const x = clamped * (NEW_THERMAL_RGB.length - 1);
  const i = Math.min(Math.floor(x), NEW_THERMAL_RGB.length - 2);
  const f = x - i;
  const a = NEW_THERMAL_RGB[i];
  const b = NEW_THERMAL_RGB[i + 1];
  const r = Math.round(a[0] + (b[0] - a[0]) * f);
  const g = Math.round(a[1] + (b[1] - a[1]) * f);
  const bl = Math.round(a[2] + (b[2] - a[2]) * f);
  return `rgb(${r},${g},${bl})`;
}

/** Boolean state colour: 1 → green (present/closed/on), 0 → red, null → grey. */
function stateColor(v: number | null): string {
  return v === 1 ? "#16a34a" : v === 0 ? "#dc2626" : GREY;
}

const abs = (v: number) => Math.abs(v);
/** 'true'/'false' (+ 'undefined'/None) → 1/0/null. */
const boolState = (r: unknown): number | null =>
  r === "true" || r === true ? 1 : r === "false" || r === false ? 0 : null;

// ── Enterprise puck (flat FIREHOSE chip columns) ──────────────────────
const HTS221: ChannelSpec[] = [
  { label: "Temperature", unit: "°C", kind: "gradient", subtype: "Temp", column: "hts221", field: "temperature", colorFor: thermal(20, 40) },
  { label: "Humidity", unit: "%", kind: "gradient", subtype: "Humidity", column: "hts221", field: "relative_humidity", palette: "sequential", colorFor: thermal(30, 70) },
];
const TSL2591: ChannelSpec[] = [
  { label: "Light", unit: "lux", kind: "gradient", subtype: "TSL2591", column: "tsl2591", field: "lux", palette: "sequential", colorFor: thermal(0, 300) },
];
const WIFI: ChannelSpec[] = [
  { label: "Wi-Fi signal", unit: "dBm", kind: "gradient", subtype: "WIFI", column: "deviceOriginRssi", transform: abs, palette: "sequential", colorFor: thermal(0, 100) },
  { label: "Wi-Fi latency", unit: "s", kind: "gradient", subtype: "deviceOriginLatency", column: "deviceOriginLatency", palette: "sequential", colorFor: thermal(0, 0.2) },
];
const CALIBRATED_PUCKS: ChannelSpec[] = [
  { label: "Calibrated pucks", unit: "", kind: "state", subtype: "calibratedPucks", column: "calibratedPucks", stateOf: boolState, stateLabels: ["Absent", "Present"], colorFor: stateColor },
];
const ANALOG_PUCKMAT: ChannelSpec[] = [
  { label: "Puck mat", unit: "", kind: "state", subtype: "analogPuckmat", column: "analogPuckmat", stateOf: boolState, stateLabels: ["Absent", "Present"], colorFor: stateColor },
];
const ANALOG_SPANISHMAT: ChannelSpec[] = [
  { label: "Spanish mat", unit: "", kind: "state", subtype: "analogSpanishmat", column: "analogSpanishmat", stateOf: boolState, stateLabels: ["Absent", "Present"], colorFor: stateColor },
];

// ── Shelly (single `serviceData` blob per row) ────────────────────────
const TEMPERATURE_HUMIDITY: ChannelSpec[] = [
  // OMON-220: 16–24°C saturated for any elevated reading (34 and 44 looked
  // identical). Widen to 10–45°C so room temps AND high readings are distinct.
  { label: "Temperature", unit: "°C", kind: "gradient", subtype: "shellyTemp", column: "serviceData", field: "temperature", colorFor: thermal(10, 45) },
  { label: "Humidity", unit: "%", kind: "gradient", subtype: "shellyHumidity", column: "serviceData", field: "humidity", palette: "sequential", colorFor: thermal(30, 70) },
];
const POWER_PLUG: ChannelSpec[] = [
  { label: "Power", unit: "W", kind: "gradient", subtype: "shellyWatts", column: "serviceData", field: "apower", palette: "sequential", colorFor: thermal(0, 200) },
  // Output state: 'true' → on (green), 'false' → off (red), absent → grey.
  { label: "Output", unit: "", kind: "state", subtype: "shellyOutput", column: "serviceData", field: "output", stateOf: boolState, stateLabels: ["Off", "On"], colorFor: stateColor },
];
const DOOR_WINDOW: ChannelSpec[] = [
  // OMON-320: BTHome spec polarity — object 0x2D `window`: 1 = open, 0 = closed.
  // Confirmed by a live reed-switch test on JG/pilot24 (28 Aug 2026): held
  // closed reported window=0, opened reported window=1. Consistent with DKL's
  // overnight narrative and VG's front door (window=0 for 99.6% of 5 days).
  // This reverses OMON-232, whose premise (window=0 = physically open) was a
  // mismeasurement. Map: window=1 (open) → state 0 → red "Open"; window=0
  // (closed) → state 1 → green "Closed". The live alerting path already uses
  // spec polarity, so this brings the display into agreement with the alerts.
  { label: "Door / window", unit: "", kind: "state", column: "serviceData", subtype: "doorWindow", field: "window", stateOf: (r) => (typeof r === "number" ? (r ? 0 : 1) : null), stateLabels: ["Open", "Closed"], colorFor: stateColor },
  // OMON-238: door opens are brief (seconds) and vanish under the CF's ~30-min
  // state bucketing, so they ride the CF's UNBUCKETED event query instead —
  // the same path as buttons and motion. window===1 = OPEN (BTHome spec, OMON-320).
  //
  // MONO-146 step 2 (2026-08-21): this lane used to read Firestore `_values`
  // (`firebaseEvents: true`), written per-reading by the per-EoC brain. It now
  // takes `eventKind: "door"` from the CF, which derives open-EDGES in SQL with
  // LAG. That was the last consumer gating the brain's telemetry writer, so its
  // retirement (steps 3-4) is unblocked.
  //
  // ☠️ NOT a like-for-like swap — it is MORE faithful, in two directions:
  //   - `_values` dotted every window==0 READING; the CF dots each OPENING.
  //   - the firehose holds edges at event resolution (measured: 129 window
  //     transitions across 9 devices in 36h, intervals down to 0-3s), so
  //     opens that the 30-min bucketing dropped now appear. Expect the lane to
  //     gain marks, not lose them.
  //
  // OMON-241: ordered AFTER the state lane (Ed's preference).
  // subtype MUST differ from the "Door / window" state lane above: subtype is the
  // sole discriminator in filterKey (`${propertyPath}::${subtype}`), so a shared
  // subtype collapses both lanes onto one filter tickbox (toggling one moved both,
  // the other read inert). subtype is used ONLY for filterKey, never data/event
  // lookup (that's eventKind), so a distinct value is safe.
  { label: "Opened", unit: "", kind: "event", subtype: "doorOpenedEvent", eventKind: "door", column: "serviceData", colorFor: () => "rgb(220,0,0)" },
];

// ── Event lanes (scatter marks, fed by the CF `events` response) ──────
// Colours per spec §5: button blue, motion red, push green. `column` is unused
// for events (they read `events[deviceId]`, not the bucketed `f` map).
const BUTTONS: ChannelSpec[] = [
  { label: "Emergency button", unit: "", kind: "event", subtype: "BUTTONS", eventKind: "button", column: "", colorFor: () => "rgb(0,100,255)" },
];
const MOTION: ChannelSpec[] = [
  { label: "Motion", unit: "", kind: "event", subtype: "motion", eventKind: "motion", column: "", colorFor: () => "rgb(255,0,0)" },
];
// The crash mat (deviceType gyromesh, catalogue type crashmat, 2026-11-01). Its
// detector decides on the device; Connect shows what it decided.
//   matMotion   each MOTION event (<role>_motion: crashmat_* on a client,
//               bedtest_* on the bench) as a mark.
//   matPresence the detector state, read from the 1 s summaries' deviceState:
//               ARMED (watching, nothing on the mat) green, ALERT (movement or
//               someone lying on it, held until the carer's button) red,
//               SETTLING grey; plus a mark for each WEIGHT event.
const MAT_MOTION: ChannelSpec[] = [
  { label: "Mat movement", unit: "", kind: "event", subtype: "matMotion", eventKind: "matMotion", column: "", colorFor: () => "rgb(255,0,0)" },
];
const matState = (r: unknown): number | null => (r === "ARMED" ? 1 : r === "ALERT" ? 0 : null);
const MAT_PRESENCE: ChannelSpec[] = [
  { label: "Mat", unit: "", kind: "state", subtype: "matPresence", column: "deviceState", stateOf: matState, stateLabels: ["Alert (movement or weight)", "Clear"], colorFor: stateColor },
  // subtype distinct from the state lane (filterKey rule, see "Opened" above)
  { label: "Someone on the mat", unit: "", kind: "event", subtype: "matWeightEvent", eventKind: "matWeight", column: "", colorFor: () => "rgb(220,0,0)" },
];
// Shelly Presence G4 (mmWave radar, 2026-11-01). A script on the sensor decides
// and publishes decisions only (serviceData bedState / bedExit; positions never
// leave the home). ☠ Best-effort by design (PLAN.md, Ed D1/D3): "In bed" HOLDS
// until a got-up is seen, so a missed exit leaves it showing "in bed"; it shows
// the BED, not a person (a partner or pet keeps it "in"). It never pages anyone.
const bedState = (r: unknown): number | null => (r === "in" ? 1 : r === "out" ? 0 : null);
const IN_BED: ChannelSpec[] = [
  { label: "In bed (radar estimate)", unit: "", kind: "state", subtype: "bedState", column: "serviceData", field: "bedState", stateOf: bedState, stateLabels: ["Out of bed", "In bed"], colorFor: stateColor },
];
const GOT_UP: ChannelSpec[] = [
  { label: "Got up", unit: "", kind: "event", subtype: "bedExit", eventKind: "bedExit", column: "", colorFor: () => "rgb(217,119,6)" },
];
const PUSH_NOTIFICATION: ChannelSpec[] = [
  // The alert-RAISED lane — "the system noticed", said independently of whether
  // anyone was told. FIRST of the three so the row reads left-to-right as the
  // actual sequence: raised → delivered → accepted.
  //
  // WHY IT EXISTS (2026-09-02): until now the timeline's only mark was the green
  // "Push sent" dot, which means "a push COMPLETED to at least one registered,
  // on-shift carer". For an EoC with no carer app that reads as a silent sensor
  // rather than an unheard alarm — pilot20 looked like it stopped working on
  // 2026-08-21 when in fact its trigger rate went UP and its recipients went to
  // zero (the OMON-304 tokensByDevice contract). Measured over 2026-08-03 →
  // 09-02: 8,742 of 18,033 alerts (48%) were raised with nobody reachable and
  // left no mark at all.
  //
  // Written by agent.notifications' write_alert_raised_dot into this SAME
  // _values collection, before audience resolution, so it reuses the identical
  // firebaseEvents machinery — only the match field and colour differ. Needs a
  // prod composite index values(alertRaised, timestamp DESC), same shape as its
  // two siblings; until it exists loadFirebaseEvents' catch degrades this lane
  // to empty (no tab breakage), so the code is safe to ship ahead of the index.
  { label: "Alert raised", unit: "", kind: "event", subtype: "alertRaised", eventKind: "push", firebaseEvents: true, firebaseMatch: { field: "alertRaised", value: true }, column: "", colorFor: () => "rgb(217,119,6)" },
  // 2026-07-08 (refined): match `pushNotificationSent == true` — TYPED boolean.
  // Only agent.pushnotifications' markPushnotificationSent writes boolean true,
  // one doc per actual FCM send: exactly the lane's semantics (and exactly what
  // the old Flutter coordinator queried). Everything else in the collection is
  // the brain's per-packet copy of the firmware's field, which arrives as the
  // STRING 'false' (~2.7k docs/day/device) — a typed equality never matches it.
  // The earlier deviceEventType=="events" filter also caught the brain's
  // event-packet copies (dots with no actual send).
  //
  // 2026-10-27: WHO was told. agent.notifications stamps `audience` ("carer" |
  // "family") and writes at most one dot per alert per audience. A family push
  // is drawn in the Family-confirmed magenta, a carer push in green, so the
  // office reads "who was told" and "who acted" in the same colours. Dots from
  // before the field existed have no audience and stay green (they were carer
  // pushes: the family leg never wrote a dot until 2026-10-27).
  { label: "Push sent", unit: "", kind: "event", subtype: "pushNotificationSent", eventKind: "push", firebaseEvents: true, firebaseMatch: { field: "pushNotificationSent", value: true }, firebaseValue: (d) => (d.audience === "family" ? 2 : 1), column: "", colorFor: (v) => (v === 2 ? "rgb(157,10,109)" : "rgb(0,200,6)") },
  // OMON-260 §C: carer-accepted dot. The engine's write_accepted_dot projects
  // `carerAcknowledged == true` into this SAME _values collection (one doc per
  // carer Confirm), so it reuses the identical firebaseEvents machinery — only
  // the match field and colour differ. Own lane row (a distinct subtype, per the
  // filterKey rule above), rendered a step to the right of the green "Push sent"
  // as the human-confirmed terminal. Needs a prod composite index
  // values(carerAcknowledged, timestamp DESC) — same shape as the push-sent one;
  // until it exists, loadFirebaseEvents' catch degrades this lane to empty (no
  // tab breakage), so the code is safe to ship ahead of the index.
  { label: "Carer accepted", unit: "", kind: "event", subtype: "carerAcknowledged", eventKind: "push", firebaseEvents: true, firebaseMatch: { field: "carerAcknowledged", value: true }, column: "", colorFor: () => "rgb(13,148,136)" },
  // ADR-0030 Am.6: a Circle of Support member's Confirm. The hub writes
  // `familyAcknowledged` (never carerAcknowledged) into the same _values
  // collection, so the office sees the family answered, not a carer. Connect
  // magenta (#9d0a6d, brand/README.md). Index values(familyAcknowledged,
  // timestamp DESC); until it builds the lane degrades to empty.
  { label: "Family confirmed", unit: "", kind: "event", subtype: "familyAcknowledged", eventKind: "push", firebaseEvents: true, firebaseMatch: { field: "familyAcknowledged", value: true }, column: "", colorFor: () => "rgb(157,10,109)" },
];

/** Property type (Firestore registry) → ordered renderable channels. */
const CHANNELS: Record<string, ChannelSpec[]> = {
  HTS221,
  TSL2591,
  WIFI,
  calibratedPucks: CALIBRATED_PUCKS,
  analogPuckmat: ANALOG_PUCKMAT,
  analogSpanishmat: ANALOG_SPANISHMAT,
  temperatureHumidity: TEMPERATURE_HUMIDITY,
  powerPlug: POWER_PLUG,
  doorWindow: DOOR_WINDOW,
  BUTTONS,
  motion: MOTION,
  matMotion: MAT_MOTION,
  matPresence: MAT_PRESENCE,
  bedState: IN_BED,
  bedExit: GOT_UP,
  pushNotificationSent: PUSH_NOTIFICATION,
};

export function channelsFor(propertyType: string): ChannelSpec[] {
  return CHANNELS[propertyType] ?? [];
}
